import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { AppUser } from 'Src/views/App/App';
import { Loader } from 'Src/components/Loader/Loader';
import { getAllHouses } from 'Src/helpers/service/admin/houses';
import { getAllForms } from 'Src/helpers/service/admin/forms';
import {
  getHousePointsLeaderboard,
  getHousePointsLeaderboardCount,
  getStudentAwardsForAcademicYear,
  LeaderboardRow,
  HouseBoard
} from 'Src/helpers/service/admin/awardLeaderboards';
import { getAllAwardEventPhotos } from 'Src/helpers/service/admin/awardEventPhotos';
import { getAllAwardEventVideos } from 'Src/helpers/service/admin/awardEventVideos';
import { AcademicYear, getAwardAcademicYears, getHouseTotals } from 'Src/helpers/service/admin/awards';
import { loadHiddenColumns, saveHiddenColumns } from 'Src/helpers/columnVisibility/columnVisibility';
import { SearchMultiSelectFilter } from 'Src/components/SearchMultiSelectFilter/SearchMultiSelectFilter';
import './AwardEventsHouseRace.scss';

interface Props {
  user: AppUser;
}

type HouseRecord = {
  id: string;
  name: string;
  status?: string;
  pic?: string;
  colors?: string[];
  primaryColour?: string;
  primaryColor?: string;
  colour?: string;
  color?: string;
};

type HouseStat = HouseRecord & {
  totalPoints: number;
  awardCount: number;
  studentCount: number;
};

type MediaItem = {
  id: string;
  picUrl?: string;
  name?: string;
  fileName?: string;
  streamData?: Array<{ url?: string }>;
  status?: string;
};

const PAGE_SIZE = 10;
const FALLBACK_IMAGE = '/dist/images/empty_pic_uploader_box.png';
const getCurrentAcademicYearLabel = () => {
  const now = new Date();
  const startYear = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
  return `${startYear}/${startYear + 1}`;
};
const HOUSE_RACE_COLUMNS_VIEW_KEY = 'awards.houseRace';
const HOUSE_RACE_COLUMNS = [
  { field: 'student', label: 'Student', required: true },
  { field: 'form', label: 'Reg form' },
  { field: 'yearGroup', label: 'Year group' },
  { field: 'house', label: 'House' },
  { field: 'awardCount', label: 'Awards' },
  { field: 'points', label: 'Points' }
];

const GLYPHS = {
  star: 'M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.8 5.9 20.6l1.4-6.8L2.2 9.1l6.9-.8z',
  trophy: 'M8 21h8M12 17v4M7 4h10v4a5 5 0 0 1-10 0V4zM5 4H3v2a3 3 0 0 0 3 3M19 4h2v2a3 3 0 0 1-3 3',
  image: 'M3 5h18v14H3zM3 15l5-5 4 4 3-3 6 6M9 9a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3',
  video: 'M23 7l-7 5 7 5zM1 5h15v14H1z',
  flame: 'M12 2s5 4 5 9a5 5 0 0 1-10 0c0-1.5.7-2.8 1.5-3.7C9 9 9.5 10 10 10.5c0-2 1-4.5 2-8.5z',
  book: 'M22 9 12 4 2 9l10 5 10-5zM6 11.5V17c0 1.1 2.7 3 6 3s6-1.9 6-3v-5.5M22 9v6',
  heart: 'M20.8 5.6a5 5 0 0 0-7-.2L12 6.9l-1.7-1.5a5 5 0 1 0-6.7 7.4l7.6 7.6a1 1 0 0 0 1.5 0l7.6-7.6a5 5 0 0 0 .1-7.2z',
  hands:
    'M8 13V5a1.5 1.5 0 0 1 3 0v6M11 11V4a1.5 1.5 0 0 1 3 0v7M14 11V6a1.5 1.5 0 0 1 3 0v8a6 6 0 0 1-6 6H9a5 5 0 0 1-4-2l-3-4a1.6 1.6 0 0 1 2.5-2L8 12',
  bolt: 'M13 2 3 14h7l-1 8 10-12h-7z',
  filter: 'M4 6h16M7 12h10M10 18h4',
  columns: 'M4 5h16v14H4zM9 5v14M15 5v14',
  close: 'M4 4l16 16M20 4 4 20'
};

const Icon: React.FC<{ path: string; size?: number; filled?: boolean; strokeWidth?: number }> = ({
  path,
  size = 16,
  filled = false,
  strokeWidth = 1.85
}) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d={path} />
  </svg>
);

const toDisplay = (row: LeaderboardRow) => row.displayName || `${row.firstName || ''} ${row.lastName || ''}`.trim();

const safeUrl = (u?: string | null) => (u ? (u.startsWith('//') ? `https:${u}` : u) : undefined);

const getInitials = (row: LeaderboardRow) => {
  const fn = (row.firstName || '').trim();
  const ln = (row.lastName || '').trim();
  if (fn || ln) return `${fn[0] ?? ''}${ln[0] ?? ''}`.toUpperCase() || '?';
  const dn = (row.displayName || '').trim();
  if (!dn) return '?';
  const parts = dn.split(/\s+/);
  return `${parts[0]?.[0] ?? ''}${parts.length > 1 ? parts[parts.length - 1][0] : ''}`.toUpperCase() || '?';
};

const getHouseColor = (house?: HouseRecord | null) => {
  const candidates = [house?.colors?.[0], house?.primaryColour, house?.primaryColor, house?.colour, house?.color];
  return candidates.find(color => typeof color === 'string' && color.trim()) || '#227ebd';
};

const getAwardTone = (name?: string) => {
  const value = String(name || '').toLowerCase();
  if (value.indexOf('match') !== -1 || value.indexOf('trophy') !== -1) {
    return { color: '#227ebd', glyph: GLYPHS.trophy };
  }
  if (value.indexOf('sport') !== -1) return { color: '#2f9e5a', glyph: GLYPHS.hands };
  if (value.indexOf('academic') !== -1) return { color: '#7b5bd6', glyph: GLYPHS.book };
  if (value.indexOf('community') !== -1) return { color: '#d64550', glyph: GLYPHS.heart };
  if (value.indexOf('conduct') !== -1) return { color: '#0f8a8a', glyph: GLYPHS.bolt };
  if (value.indexOf('effort') !== -1) return { color: '#f0932b', glyph: GLYPHS.flame };
  return { color: '#227ebd', glyph: GLYPHS.star };
};

export default function AwardEventsHouseRace({ user }: Props) {
  const [loadingMaster, setLoadingMaster] = useState(true);
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingGlobal, setLoadingGlobal] = useState(false);
  const [loadingHouse, setLoadingHouse] = useState(false);

  const [globalItemsRaw, setGlobalItemsRaw] = useState<LeaderboardRow[]>([]);
  const [globalCount, setGlobalCount] = useState(0);
  const [globalPage, setGlobalPage] = useState(1);

  const [activeHouseItemsRaw, setActiveHouseItemsRaw] = useState<LeaderboardRow[]>([]);
  const [activeHouseCount, setActiveHouseCount] = useState(0);
  const [housePageById, setHousePageById] = useState<Record<string, number>>({});
  const [activeHouseId, setActiveHouseId] = useState<string | null>(null);

  const [housesById, setHousesById] = useState<Record<string, HouseRecord>>({});
  const [houseStatsById, setHouseStatsById] = useState<Record<string, HouseStat>>({});
  const [formsById, setFormsById] = useState<Record<string, any>>({});
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]);
  const [academicYearId, setAcademicYearId] = useState('');

  const [selected, setSelected] = useState<LeaderboardRow | null>(null);
  const [awards, setAwards] = useState<any[]>([]);
  const [isAwardsLoading, setIsAwardsLoading] = useState(false);

  const [openGalleryForAward, setOpenGalleryForAward] = useState<any | null>(null);
  const [galleryPhotos, setGalleryPhotos] = useState<MediaItem[]>([]);
  const [galleryVideos, setGalleryVideos] = useState<MediaItem[]>([]);
  const [isGalleryLoading, setIsGalleryLoading] = useState(false);

  const [searchValue, setSearchValue] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [draftFormIds, setDraftFormIds] = useState<string[]>([]);
  const [appliedFormIds, setAppliedFormIds] = useState<string[]>([]);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isColumnsOpen, setIsColumnsOpen] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>(() =>
    loadHiddenColumns(user, HOUSE_RACE_COLUMNS_VIEW_KEY).filter(
      field => field !== 'student' && HOUSE_RACE_COLUMNS.some(column => column.field === field)
    )
  );
  const columnsRef = useRef<HTMLDivElement>(null);

  const nf = useMemo(() => new Intl.NumberFormat(), []);
  const currentHousePage = activeHouseId ? housePageById[activeHouseId] || 1 : 1;

  const decorateWith = (
    rows: LeaderboardRow[],
    hById: Record<string, HouseRecord>,
    fById: Record<string, any>
  ): LeaderboardRow[] =>
    (rows || []).map(row => {
      const form = row.formId ? fById[row.formId] : undefined;
      return {
        ...row,
        formName: form?.name ?? row.formName ?? null,
        yearGroupName: form?.ageGroup ?? row.yearGroupName ?? null,
        houseName: row.houseId ? hById[row.houseId]?.name ?? row.houseName ?? null : null
      };
    });

  const pluralize = (n: number, one: string, many: string) => `${nf.format(n)} ${Math.abs(n) === 1 ? one : many}`;

  const houses = useMemo(() => {
    if (!academicYearId) return [];
    return Object.keys(housesById)
      .map(id => housesById[id])
      .filter(house =>
        academicYearId === getCurrentAcademicYearLabel()
          ? house.status === 'ACTIVE'
          : house.status === 'ACTIVE' || house.status === 'ARCHIVED'
      );
  }, [housesById, academicYearId]);
  const forms = useMemo(
    () =>
      Object.keys(formsById)
        .map(id => formsById[id])
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))),
    [formsById]
  );
  const appliedFormIdsKey = appliedFormIds.join('|');
  const leaderboardWhere = useMemo(() => {
    const where: any = {};
    where.academicYearId = academicYearId;
    if (appliedSearch) where.search = appliedSearch;
    if (appliedFormIds.length) where.formId = { $in: appliedFormIds };
    return where;
  }, [academicYearId, appliedSearch, appliedFormIdsKey]);

  const sortedHouseStats = useMemo(() => {
    return houses
      .map(house => houseStatsById[house.id] || { ...house, totalPoints: 0, awardCount: 0, studentCount: 0 })
      .sort((a, b) => b.totalPoints - a.totalPoints || a.name.localeCompare(b.name));
  }, [houses, houseStatsById]);

  useEffect(() => {
    const onMouseDown = (event: MouseEvent) => {
      if (isColumnsOpen && columnsRef.current && !columnsRef.current.contains(event.target as Node)) {
        setIsColumnsOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setIsColumnsOpen(false);
      if (isFiltersOpen) {
        setDraftFormIds(appliedFormIds);
        setIsFiltersOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isColumnsOpen, isFiltersOpen, appliedFormIdsKey]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingMaster(true);
        const [housesList, forms, years] = await Promise.all([
          getAllHouses(user, { status: { $in: ['ACTIVE', 'ARCHIVED'] } }),
          getAllForms(user),
          getAwardAcademicYears(user)
        ]);
        if (cancelled) return;

        const byId = (housesList || []).reduce((memo, house) => {
          memo[house.id] = house;
          return memo;
        }, {} as Record<string, HouseRecord>);
        const fById = (forms || []).reduce((memo, form) => {
          memo[form.id] = form;
          return memo;
        }, {} as Record<string, any>);

        setHousesById(byId);
        setFormsById(fById);
        const availableYears = years || [];
        const currentAcademicYear = getCurrentAcademicYearLabel();
        const initialAcademicYear = availableYears.some(year => year.id === currentAcademicYear)
          ? currentAcademicYear
          : availableYears[0]?.id || '';
        setAcademicYears(availableYears);
        setAcademicYearId(initialAcademicYear);
        const initialHouse = (housesList || []).find(house => house.status === 'ACTIVE') || housesList?.[0];
        if (!activeHouseId && initialHouse?.id) setActiveHouseId(initialHouse.id);
      } catch (e) {
        console.error('[HouseRace] master load failed', e);
      } finally {
        if (!cancelled) setLoadingMaster(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (activeHouseId && houses.some(house => house.id === activeHouseId)) return;
    setActiveHouseId(houses[0]?.id || null);
  }, [academicYearId, housesById]);

  useEffect(() => {
    if (!academicYearId || houses.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingStats(true);
        const totals = await getHouseTotals(user, academicYearId);
        const totalsByHouse = (totals || []).reduce((memo, row) => {
          memo[String(row.houseId)] = row;
          return memo;
        }, {} as Record<string, { totalPoints: number; awardCount: number }>);
        const stats = houses.map(house => ({
          ...house,
          totalPoints: Number(totalsByHouse[house.id]?.totalPoints) || 0,
          awardCount: Number(totalsByHouse[house.id]?.awardCount) || 0,
          studentCount: 0
        }));
        if (cancelled) return;
        setHouseStatsById(
          stats.reduce((memo, stat) => {
            memo[stat.id] = stat;
            return memo;
          }, {} as Record<string, HouseStat>)
        );
      } catch (e) {
        console.error('[HouseRace] house stats load failed', e);
      } finally {
        if (!cancelled) setLoadingStats(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, housesById, academicYearId]);

  useEffect(() => {
    if (!academicYearId) {
      setGlobalItemsRaw([]);
      setGlobalCount(0);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingGlobal(true);
        const where = Object.keys(leaderboardWhere).length ? leaderboardWhere : undefined;
        const [items, { count }] = await Promise.all([
          getHousePointsLeaderboard(user, { where, limit: PAGE_SIZE, skip: (globalPage - 1) * PAGE_SIZE }),
          getHousePointsLeaderboardCount(user, where)
        ]);
        if (cancelled) return;
        setGlobalItemsRaw(items || []);
        setGlobalCount(count || 0);
      } catch (e) {
        console.error('[HouseRace] global load failed', e);
      } finally {
        if (!cancelled) setLoadingGlobal(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, globalPage, academicYearId, appliedSearch, appliedFormIdsKey]);

  useEffect(() => {
    if (!academicYearId || !activeHouseId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingHouse(true);
        const where = { ...leaderboardWhere, houseId: activeHouseId };
        const [items, { count }] = await Promise.all([
          getHousePointsLeaderboard(user, { where, limit: PAGE_SIZE, skip: (currentHousePage - 1) * PAGE_SIZE }),
          getHousePointsLeaderboardCount(user, where)
        ]);
        if (cancelled) return;
        setActiveHouseItemsRaw(items || []);
        setActiveHouseCount(count || 0);
      } catch (e) {
        console.error('[HouseRace] house load failed', e);
      } finally {
        if (!cancelled) setLoadingHouse(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, activeHouseId, currentHousePage, academicYearId, appliedSearch, appliedFormIdsKey]);

  const globalItems = useMemo(() => decorateWith(globalItemsRaw, housesById, formsById), [
    globalItemsRaw,
    housesById,
    formsById
  ]);

  const activeHouseBoard: HouseBoard | null = useMemo(() => {
    if (!activeHouseId) return null;
    return {
      houseId: activeHouseId,
      houseName: housesById[activeHouseId]?.name || '',
      items: decorateWith(activeHouseItemsRaw, housesById, formsById),
      count: activeHouseCount
    };
  }, [activeHouseId, activeHouseItemsRaw, activeHouseCount, housesById, formsById]);

  const houseLogo = (houseId?: string | null) => {
    if (!houseId) return FALLBACK_IMAGE;
    return housesById[houseId]?.pic || user.activeSchool?.pic || FALLBACK_IMAGE;
  };

  const houseColor = (houseId?: string | null) => getHouseColor(houseId ? housesById[houseId] : null);

  const onStudentClick = async (row: LeaderboardRow) => {
    setSelected(row);
    setAwards([]);
    setIsAwardsLoading(true);
    try {
      const studentKey = (row as any).userId || (row as any).studentUserId || row.studentId;
      const opts = { ...(row.houseId ? { houseId: row.houseId } : {}), academicYearId };
      const list = (await getStudentAwardsForAcademicYear(user, studentKey, opts)) || [];
      setAwards(
        list.map(evt => {
          const isHousePoints = String(evt?.awardType?.name || '').toLowerCase() === 'house points';
          const housePic = evt?.houseId ? housesById[evt.houseId]?.pic : undefined;
          return {
            ...evt,
            awardType: {
              ...(evt.awardType || {}),
              picture: evt?.awardType?.picture || (isHousePoints ? housePic : undefined)
            }
          };
        })
      );
    } catch (e) {
      console.error('[HouseRace] student awards load failed', e);
    } finally {
      setIsAwardsLoading(false);
    }
  };

  const openAwardGallery = async (award: any) => {
    setOpenGalleryForAward(award);
    setGalleryPhotos([]);
    setGalleryVideos([]);
    setIsGalleryLoading(true);
    try {
      const [rawPhotos, rawVideos] = await Promise.all([
        getAllAwardEventPhotos(user, award.id).catch(() => []),
        getAllAwardEventVideos(user, award.id).catch(() => [])
      ]);
      setGalleryPhotos(Array.isArray(rawPhotos) ? rawPhotos : rawPhotos?.items || []);
      setGalleryVideos(Array.isArray(rawVideos) ? rawVideos : rawVideos?.items || []);
    } catch (e) {
      console.error('[HouseRace] gallery load failed', e);
    } finally {
      setIsGalleryLoading(false);
    }
  };

  const closeStudentDrawer = () => {
    setSelected(null);
    setAwards([]);
  };

  const closeGallery = () => {
    setOpenGalleryForAward(null);
    setGalleryPhotos([]);
    setGalleryVideos([]);
  };

  const resetLeaderboardPages = () => {
    setGlobalPage(1);
    setHousePageById({});
  };

  const applySearch = () => {
    setAppliedSearch(searchValue.trim());
    resetLeaderboardPages();
  };

  const openFilters = () => {
    setDraftFormIds(appliedFormIds);
    setIsColumnsOpen(false);
    setIsFiltersOpen(true);
  };

  const closeFilters = () => {
    setDraftFormIds(appliedFormIds);
    setIsFiltersOpen(false);
  };

  const applyFilters = () => {
    setAppliedFormIds(draftFormIds);
    setIsFiltersOpen(false);
    resetLeaderboardPages();
  };

  const clearAllFilters = () => {
    setSearchValue('');
    setAppliedSearch('');
    setDraftFormIds([]);
    setAppliedFormIds([]);
    setIsFiltersOpen(false);
    resetLeaderboardPages();
  };

  const removeFormFilter = (formId: string) => {
    const next = appliedFormIds.filter(id => id !== formId);
    setAppliedFormIds(next);
    setDraftFormIds(next);
    resetLeaderboardPages();
  };

  const toggleColumn = (field: string) => {
    if (field === 'student') return;
    const next = hiddenColumns.includes(field)
      ? hiddenColumns.filter(column => column !== field)
      : [...hiddenColumns, field];
    saveHiddenColumns(user, HOUSE_RACE_COLUMNS_VIEW_KEY, next);
    setHiddenColumns(next);
  };

  const resetColumns = () => {
    saveHiddenColumns(user, HOUSE_RACE_COLUMNS_VIEW_KEY, []);
    setHiddenColumns([]);
  };

  const isColumnVisible = (field: string) => !hiddenColumns.includes(field);

  const renderAvatar = (row: LeaderboardRow, useHouseLogo = false) => {
    const accent = houseColor(row.houseId);
    const avatarUrl = safeUrl(row.avatar);
    if (useHouseLogo) {
      return <img src={houseLogo(row.houseId)} alt={row.houseName || 'House'} />;
    }
    if (avatarUrl) return <img src={avatarUrl} alt={toDisplay(row)} />;
    return <span style={{ borderColor: accent, color: accent }}>{getInitials(row)}</span>;
  };

  const renderStudentRow = (row: LeaderboardRow, rank: number, options?: { houseMode?: boolean }) => {
    const display = toDisplay(row);
    const color = houseColor(row.houseId);
    return (
      <button
        key={`${row.houseId || 'global'}-${row.studentId}`}
        type="button"
        className="hrStudentRow"
        onClick={() => onStudentClick(row)}
      >
        <span
          className={rank <= 3 ? 'hrStudentRow__rank hrStudentRow__rank--top' : 'hrStudentRow__rank'}
          style={{ color }}
        >
          {rank}
        </span>
        <span className="hrStudentRow__avatar">
          {renderAvatar(row, !options?.houseMode)}
          <i style={{ backgroundColor: color }} />
        </span>
        <span className="hrStudentRow__main">
          <strong>{display}</strong>
          <span>
            {isColumnVisible('form') && row.formName && <em>{row.formName}</em>}
            {isColumnVisible('yearGroup') && row.yearGroupName && <em>{row.yearGroupName}</em>}
            {isColumnVisible('house') && row.houseName && <em>{row.houseName}</em>}
          </span>
        </span>
        {(isColumnVisible('points') || isColumnVisible('awardCount')) && (
          <span className="hrStudentRow__points">
            {isColumnVisible('points') && (
              <strong style={{ color }}>
                {nf.format(Number(row.totalPoints) || 0)}
                <small> pts</small>
              </strong>
            )}
            {isColumnVisible('awardCount') && <em>{pluralize(Number(row.awardCount) || 0, 'award', 'awards')}</em>}
          </span>
        )}
      </button>
    );
  };

  const renderPagination = (page: number, total: number, onPage: (page: number) => void) => {
    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
    return (
      <div className="hrPagination">
        <span>
          Page {page} of {pageCount}
        </span>
        <div>
          <button type="button" disabled={page <= 1} onClick={() => onPage(1)}>
            First
          </button>
          <button type="button" disabled={page <= 1} onClick={() => onPage(Math.max(1, page - 1))}>
            Previous
          </button>
          <button type="button" disabled={page >= pageCount} onClick={() => onPage(Math.min(pageCount, page + 1))}>
            Next
          </button>
          <button type="button" disabled={page >= pageCount} onClick={() => onPage(pageCount)}>
            Last
          </button>
        </div>
      </div>
    );
  };

  const renderLeaderboardControls = () => {
    const hasActiveFilters = Boolean(appliedSearch || appliedFormIds.length);
    return (
      <section className="hrCard hrLeaderboardControls">
        <div className="hrToolbar">
          <label className="hrSearch">
            <span>Student</span>
            <input
              value={searchValue}
              placeholder="Search by first or last name"
              onChange={event => setSearchValue(event.target.value)}
              onKeyDown={event => event.key === 'Enter' && applySearch()}
            />
          </label>
          <button
            type="button"
            className={isFiltersOpen || appliedFormIds.length ? 'hrControlButton is-active' : 'hrControlButton'}
            onClick={openFilters}
          >
            <Icon path={GLYPHS.filter} />
            Filters
            {appliedFormIds.length > 0 && <b>1</b>}
          </button>
          <div className="hrColumns" ref={columnsRef}>
            <button
              type="button"
              className={isColumnsOpen ? 'hrControlButton is-active' : 'hrControlButton'}
              onClick={() => {
                setIsFiltersOpen(false);
                setIsColumnsOpen(!isColumnsOpen);
              }}
            >
              <Icon path={GLYPHS.columns} />
              Columns
              <b>{HOUSE_RACE_COLUMNS.length - hiddenColumns.length}</b>
            </button>
            {isColumnsOpen && (
              <div className="hrColumnsMenu">
                <header>
                  <strong>Show / hide columns</strong>
                  <button type="button" onClick={resetColumns}>
                    Reset
                  </button>
                </header>
                {HOUSE_RACE_COLUMNS.map(column => (
                  <label key={column.field}>
                    <input
                      type="checkbox"
                      checked={isColumnVisible(column.field)}
                      disabled={Boolean(column.required)}
                      onChange={() => toggleColumn(column.field)}
                    />
                    {column.label}
                    {column.required && <small>Required</small>}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <div className="hrActiveFilters">
            {appliedSearch && (
              <span>
                <b>Student:</b> {appliedSearch}
                <button
                  type="button"
                  aria-label="Remove student filter"
                  onClick={() => {
                    setSearchValue('');
                    setAppliedSearch('');
                    resetLeaderboardPages();
                  }}
                >
                  ×
                </button>
              </span>
            )}
            {appliedFormIds.map(formId => (
              <span key={formId}>
                <b>Reg form:</b> {formsById[formId]?.name || 'Unknown form'}
                <button type="button" aria-label="Remove reg form filter" onClick={() => removeFormFilter(formId)}>
                  ×
                </button>
              </span>
            ))}
            <button type="button" className="hrActiveFilters__clear" onClick={clearAllFilters}>
              Clear all
            </button>
          </div>
        )}
      </section>
    );
  };

  const renderFiltersPanel = () => {
    if (!isFiltersOpen) return null;
    return (
      <div className="hrFilterBackdrop" onMouseDown={closeFilters}>
        <aside className="hrFilters" onMouseDown={event => event.stopPropagation()} aria-label="House race filters">
          <header>
            <strong>Filters</strong>
            <button type="button" onClick={closeFilters} aria-label="Close filters">
              ×
            </button>
          </header>
          <div className="hrFilters__body">
            <SearchMultiSelectFilter
              label="Reg forms"
              selected={draftFormIds}
              options={forms.map(form => ({ value: String(form.id), label: form.name }))}
              onToggle={formId =>
                setDraftFormIds(
                  draftFormIds.includes(formId) ? draftFormIds.filter(id => id !== formId) : [...draftFormIds, formId]
                )
              }
              placeholder="Search registration forms…"
              emptyText={forms.length === 0 ? 'No registration forms available' : undefined}
            />
          </div>
          <footer>
            <button type="button" className="is-clear" onClick={clearAllFilters}>
              Clear all
            </button>
            <div>
              <button type="button" onClick={closeFilters}>
                Cancel
              </button>
              <button type="button" className="is-apply" onClick={applyFilters}>
                Apply
              </button>
            </div>
          </footer>
        </aside>
      </div>
    );
  };

  const renderRaceBars = () => {
    const maxPoints = Math.max(1, ...sortedHouseStats.map(stat => stat.totalPoints));
    return (
      <section className="hrCard hrRaceCard">
        <div className="hrRaceCard__title">
          <span>
            <Icon path={GLYPHS.trophy} size={19} filled />
          </span>
          <strong>The race so far</strong>
          <em>Academic year {academicYearId}</em>
        </div>
        <div className="hrRaceBars">
          {loadingStats && sortedHouseStats.length === 0 ? (
            <Loader />
          ) : (
            sortedHouseStats.map((house, index) => {
              const color = getHouseColor(house);
              const percent = Math.max(6, Math.round((house.totalPoints / maxPoints) * 100));
              return (
                <div className="hrRaceBar" key={house.id}>
                  <span className={index < 3 ? 'hrRaceBar__rank hrRaceBar__rank--medal' : 'hrRaceBar__rank'}>
                    {index + 1}
                  </span>
                  <div>
                    <div className="hrRaceBar__meta">
                      <strong>
                        <i style={{ backgroundColor: color }} />
                        {house.name}
                      </strong>
                      <span>
                        <b style={{ color }}>{nf.format(house.totalPoints)}</b> pts · {nf.format(house.awardCount)}{' '}
                        awards
                      </span>
                    </div>
                    <div className="hrRaceBar__track">
                      <i
                        className="hrRaceBar__fill"
                        style={{ width: `${percent}%`, background: `linear-gradient(90deg, ${color}cc, ${color})` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>
    );
  };

  const renderGlobalBoard = () => {
    const start = (globalPage - 1) * PAGE_SIZE;
    return (
      <section className="hrCard hrBoard">
        <div className="hrBoard__head">
          <strong>Top students — all houses</strong>
          <span>{globalItems.length} shown</span>
        </div>
        <div className="hrBoard__body">
          {loadingGlobal ? (
            <Loader />
          ) : globalItems.length === 0 ? (
            <div className="hrEmpty">No results.</div>
          ) : (
            globalItems.map((row, idx) => renderStudentRow(row, start + idx + 1))
          )}
        </div>
        {renderPagination(globalPage, globalCount, setGlobalPage)}
      </section>
    );
  };

  const renderHouseBoard = () => {
    const active = activeHouseBoard;
    const start = (currentHousePage - 1) * PAGE_SIZE;
    return (
      <section className="hrCard hrBoard">
        <div className="hrBoard__head hrBoard__head--tabs">
          <strong>By house</strong>
          <div className="hrHouseTabs">
            {houses.map(house => {
              const color = houseColor(house.id);
              const activeTab = house.id === activeHouseId;
              return (
                <button
                  key={house.id}
                  type="button"
                  className={activeTab ? 'hrHouseTab hrHouseTab--active' : 'hrHouseTab'}
                  style={{
                    borderColor: activeTab ? color : undefined,
                    color: activeTab ? color : undefined,
                    backgroundColor: activeTab ? `${color}18` : undefined
                  }}
                  onClick={() => {
                    setActiveHouseId(house.id);
                    setHousePageById(prev => ({ ...prev, [house.id]: 1 }));
                  }}
                >
                  <i style={{ backgroundColor: color }} />
                  {house.name}
                </button>
              );
            })}
          </div>
        </div>
        <div className="hrBoard__body">
          {loadingHouse ? (
            <Loader />
          ) : !active ? (
            <div className="hrEmpty">No house selected.</div>
          ) : active.items.length === 0 ? (
            <div className="hrEmpty">No awards yet for this house.</div>
          ) : (
            active.items.map((row, idx) => renderStudentRow(row, start + idx + 1, { houseMode: true }))
          )}
        </div>
        {active &&
          renderPagination(currentHousePage, active.count, page =>
            setHousePageById(prev => ({ ...prev, [active.houseId]: page }))
          )}
      </section>
    );
  };

  const renderAwardIcon = (award: any, size = 38) => {
    const picture = award?.awardType?.picture;
    const name = award?.awardType?.name || 'Award';
    if (picture) {
      return (
        <img
          className="hrAwardIcon"
          src={picture}
          alt={name}
          style={{ width: size, height: size, borderRadius: Math.round(size / 3.8) }}
        />
      );
    }
    const tone = getAwardTone(name);
    return (
      <span
        className="hrAwardIcon"
        style={{
          width: size,
          height: size,
          borderRadius: Math.round(size / 3.8),
          backgroundColor: `${tone.color}1a`,
          color: tone.color
        }}
      >
        <Icon path={tone.glyph} size={Math.round(size * 0.52)} filled />
      </span>
    );
  };

  const renderStudentDrawer = () => {
    if (!selected) return null;
    const color = houseColor(selected.houseId);
    return (
      <aside className="hrDrawer">
        <div className="hrDrawer__head">
          <span className="hrDrawer__avatar">
            {renderAvatar(selected)}
            <i style={{ backgroundColor: color }} />
          </span>
          <div>
            <h2>{toDisplay(selected)}</h2>
            <p>
              {selected.formName || 'No form'}
              <i />
              {selected.yearGroupName || 'No year group'}
              <i />
              {selected.houseName || 'No house'}
            </p>
          </div>
          <button type="button" className="hrIconButton" onClick={closeStudentDrawer} aria-label="Close">
            <Icon path={GLYPHS.close} size={18} />
          </button>
        </div>

        <div className="hrDrawer__body">
          <div className="hrStudentStats" style={{ borderColor: `${color}30`, backgroundColor: `${color}12` }}>
            <div>
              <strong style={{ color }}>{nf.format(Number(selected.totalPoints) || 0)}</strong>
              <span>points</span>
            </div>
            <i />
            <div>
              <strong>{nf.format(Number(selected.awardCount) || 0)}</strong>
              <span>awards</span>
            </div>
          </div>

          <h3>Awards in {academicYearId}</h3>
          {isAwardsLoading ? (
            <Loader />
          ) : awards.length === 0 ? (
            <div className="hrEmpty hrEmpty--drawer">No awards yet.</div>
          ) : (
            <div className="hrAwardList">
              {awards.map(award => {
                const mediaCount =
                  Number((award as any).mediaCount || (award as any).photosCount || 0) +
                  Number((award as any).videosCount || 0);
                return (
                  <div className="hrAwardItem" key={award.id}>
                    {renderAwardIcon(award)}
                    <div>
                      <strong>{award.awardType?.name || 'Award'}</strong>
                      <span>
                        {award.awardedAt ? moment(award.awardedAt).format('DD MMM YYYY') : 'No date'} ·{' '}
                        {award.points > 0 ? '+' : ''}
                        {award.points || 0} pts
                      </span>
                      {award.reason && <p>{award.reason}</p>}
                    </div>
                    <button type="button" className="hrButton hrButton--ghost" onClick={() => openAwardGallery(award)}>
                      <Icon path={GLYPHS.image} size={14} />
                      {mediaCount || 'Gallery'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="hrDrawer__foot">
          <button type="button" className="hrButton hrButton--primary" onClick={closeStudentDrawer}>
            Close
          </button>
        </div>
      </aside>
    );
  };

  const getVideoUrl = (video: MediaItem) => (Array.isArray(video.streamData) && video.streamData[0]?.url) || '';

  const renderGalleryModal = () => {
    if (!openGalleryForAward) return null;
    const mediaCount = galleryPhotos.length + galleryVideos.length;
    return (
      <div className="hrModalOverlay" onMouseDown={closeGallery}>
        <div className="hrModal" onMouseDown={event => event.stopPropagation()} role="dialog" aria-modal="true">
          <div className="hrModal__head">
            {renderAwardIcon(openGalleryForAward, 42)}
            <div>
              <h2>Award media</h2>
              <p>
                {openGalleryForAward.awardType?.name || 'Award'} ·{' '}
                {mediaCount ? `${mediaCount} media item${mediaCount === 1 ? '' : 's'}` : 'No media yet'}
              </p>
            </div>
            <button type="button" className="hrIconButton" onClick={closeGallery} aria-label="Close">
              <Icon path={GLYPHS.close} size={18} />
            </button>
          </div>
          <div className="hrModal__body">
            {isGalleryLoading ? (
              <div className="hrMediaSkeleton">
                {Array.from({ length: 6 }).map((_, index) => (
                  <i key={index} />
                ))}
              </div>
            ) : mediaCount === 0 ? (
              <div className="hrGalleryEmpty">
                <Icon path={GLYPHS.image} size={30} />
                <span>No photos or videos on this award yet.</span>
              </div>
            ) : (
              <div className="hrGalleryGrid">
                {galleryPhotos.map(photo => (
                  <img key={photo.id} src={photo.picUrl} alt={photo.name || 'Award photo'} />
                ))}
                {galleryVideos.map(video => {
                  const url = getVideoUrl(video);
                  return url ? (
                    <video key={video.id} src={url} controls preload="metadata" />
                  ) : (
                    <div key={video.id} className="hrGalleryProcessing">
                      <Icon path={GLYPHS.video} size={22} />
                      Processing
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="hrModal__foot">
            <button type="button" className="hrButton hrButton--primary" onClick={closeGallery}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loadingMaster) {
    return (
      <div className="bHouseRace bHouseRace--loading">
        <Loader />
      </div>
    );
  }

  return (
    <div className="bHouseRace">
      <header className="hrHeader">
        <div>
          <div className="hrBreadcrumb">
            <span>Awards</span>
            <i>/</i>
            <strong>House race</strong>
          </div>
          <h1>House race</h1>
        </div>
        <div className="hrHeader__chips">
          {academicYearId && (
            <>
              <span>
                <b>{nf.format(globalCount)}</b> ranked students
              </span>
              <span>
                <b>{nf.format(houses.length)}</b> houses
              </span>
            </>
          )}
          <label className="hrAcademicYearSelect">
            <span>Academic year</span>
            <select
              value={academicYearId}
              disabled={academicYears.length === 0}
              onChange={event => {
                setAcademicYearId(event.target.value);
                setHouseStatsById({});
                setGlobalItemsRaw([]);
                setGlobalCount(0);
                setActiveHouseItemsRaw([]);
                setActiveHouseCount(0);
                setSelected(null);
                setAwards([]);
                resetLeaderboardPages();
              }}
            >
              {academicYears.length === 0 && <option value="">No award years</option>}
              {academicYears.map(year => (
                <option key={year.id} value={year.id}>
                  {year.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </header>

      {!academicYearId ? (
        <section className="hrCard hrNoAwardYears">
          <Icon path={GLYPHS.trophy} size={28} />
          <h2>No awards yet</h2>
          <p>Academic years will appear here after the school grants its first award.</p>
        </section>
      ) : (
        <>
          {renderRaceBars()}
          {renderLeaderboardControls()}

          <div className="hrBoards">
            {renderGlobalBoard()}
            {renderHouseBoard()}
          </div>

          {renderStudentDrawer()}
          {renderGalleryModal()}
          {renderFiltersPanel()}
        </>
      )}
    </div>
  );
}
